import './kreate.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { EmblaCarouselType, EmblaEventType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import { Button } from 'components/ui/button';
import blogWriterImage from './images/blog-writer.png';
import memeGeneratorImage from './images/meme-generator.png';
import podcastGeneratorImage from './images/podcast-generator.png';
import brandbookImage from './images/brandbook.png';
import botBuilderImage from './images/botbuilder.png';
import TypefaceIcon from './images/typeface-icon';
import { BookDown, Bot, MoveUpRight, PodcastIcon } from 'lucide-react';
import Robot from './images/Robot';
import { cn } from 'lib/utils';
import { getDevToolRoute } from 'lib/footer.util';

const TWEEN_FACTOR_BASE = 0.6;

const numberWithinRange = (number: number, min: number, max: number): number =>
  Math.min(Math.max(number, min), max);

const kreateItems = [
  {
    title: 'Blog Writer',
    description:
      'Transform your blog with our all-in-one tool that creates and publishes tailored copy and stunning images in seconds.',
    image: blogWriterImage,
    buttonText: 'Try Blog Writer',
    toolLink: getDevToolRoute('BLOG_WRITER'),
    icon: <TypefaceIcon className="size-12" />,
  },
  {
    title: 'Meme Bot',
    description:
      'Level up your meme game with our intelligent Meme Bot - craft viral-worthy content that stands out  in seconds.',
    image: memeGeneratorImage,
    buttonText: 'Contact',
    toolLink: '/contact ',
    icon: <Robot className="size-8" />,
  },
  {
    title: 'Podcast Generator',
    description:
      'Transform your thoughts into captivating audio. We script, edit, and produce podcasts effortlessly—perfect for sharing stories, ideas, and more.',
    image: podcastGeneratorImage,
    buttonText: 'Try Podcast',
    toolLink: getDevToolRoute('PODCAST_GENERATOR'),
    icon: <PodcastIcon className="size-8" />,
  },
  {
    title: 'Multimedia bot Builder',
    description:
      'Create engaging multimedia bots that drive conversions. Our tool helps you craft interactive telegram bots that engage and convert.',
    image: botBuilderImage,
    buttonText: 'Try Bot Builder',
    toolLink: getDevToolRoute('BOT_BUILDER'),
    icon: <Bot className="size-8" />,
  },
  {
    title: 'Brandbook',
    description:
      'Build a brand that stands out with our Brandbook tool. Create a consistent brand identity with our easy-to-use tool.',
    image: brandbookImage,
    buttonText: 'Try Brandbook',
    toolLink: getDevToolRoute('BRANDBOOK'),
    icon: <BookDown className="size-8" />,
  },
];

const KreateItem = ({ item }: { item: (typeof kreateItems)[0] }) => {
  return (
    <div className="embla__slide p-2 sm:p-4 bg-[#846CEC33] rounded-2xl sm:rounded-[1.5rem] mx-2 sm:mx-4 h-fit">
      <div className="flex flex-col sm:flex-row sm:h-[400px]">
        <div className="w-full sm:w-auto">
          <img
            alt={item.title}
            className="w-full md:h-full md:min-w-[420px] object-cover rounded-l-xl"
            src={item.image}
          />
        </div>
        <div className="flex flex-col justify-center px-4 py-6 sm:px-12 w-full bg-[#202020] text-white rounded-b-xl sm:rounded-none sm:rounded-r-[1rem]">
          <div>{item.icon}</div>
          <div className="text-2xl sm:text-4xl font-medium mt-3 sm:mt-4 mb-3 sm:mb-4">
            {item.title}
          </div>
          <div className="text-sm sm:text-base text-accent">
            {item.description}
          </div>
          <div className="mt-6 sm:mt-[2rem]">
            <Button
              className="text-sm text-primary"
              size="lg"
              variant="secondary"
              onClick={() => {
                window.open(item.toolLink, '_blank');
              }}
            >
              {item.buttonText}{' '}
              <MoveUpRight className="size-3" strokeWidth={3} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

const buttons = [
  'Blog Writer',
  'Meme Generator',
  'Podcast Generator',
  'Multimedia bot Builder',
  'Brandbook',
];

const KreateSomethingCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const tweenFactor = useRef(0);

  const setTweenFactor = useCallback((emblaApi: EmblaCarouselType) => {
    tweenFactor.current = TWEEN_FACTOR_BASE * emblaApi.scrollSnapList().length;
  }, []);

  const tweenOpacity = useCallback(
    (emblaApi: EmblaCarouselType, eventName?: EmblaEventType) => {
      const engine = emblaApi.internalEngine();
      const scrollProgress = emblaApi.scrollProgress();
      const slidesInView = emblaApi.slidesInView();
      const isScrollEvent = eventName === 'scroll';

      emblaApi.scrollSnapList().forEach((scrollSnap, snapIndex) => {
        let diffToTarget = scrollSnap - scrollProgress;
        const slidesInSnap = engine.slideRegistry[snapIndex];

        slidesInSnap?.forEach((slideIndex) => {
          if (isScrollEvent && !slidesInView.includes(slideIndex)) return;

          if (engine.options.loop) {
            engine.slideLooper.loopPoints.forEach((loopItem) => {
              const target = loopItem.target();

              if (slideIndex === loopItem.index && target !== 0) {
                const sign = Math.sign(target);

                if (sign === -1) {
                  diffToTarget = scrollSnap - (1 + scrollProgress);
                }
                if (sign === 1) {
                  diffToTarget = scrollSnap + (1 - scrollProgress);
                }
              }
            });
          }

          const tweenValue = 1 - Math.abs(diffToTarget * tweenFactor.current);
          const opacity = numberWithinRange(tweenValue, 0, 1).toString();

          emblaApi.slideNodes()[slideIndex].style.opacity = opacity;
        });
      });
    },
    []
  );

  useEffect(() => {
    if (!emblaApi) return;

    setTweenFactor(emblaApi);
    tweenOpacity(emblaApi);

    // Update current slide when the carousel changes
    const onSelect = () => {
      setCurrentSlide(emblaApi.selectedScrollSnap());
    };

    emblaApi
      .on('reInit', setTweenFactor)
      .on('reInit', tweenOpacity)
      .on('scroll', tweenOpacity)
      .on('slideFocus', tweenOpacity)
      .on('select', onSelect);

    // Cleanup
    return () => {
      emblaApi
        .off('reInit', setTweenFactor)
        .off('reInit', tweenOpacity)
        .off('scroll', tweenOpacity)
        .off('slideFocus', tweenOpacity)
        .off('select', onSelect);
    };
  }, [emblaApi, tweenOpacity]);

  const handleButtonClick = (button: string) => {
    emblaApi?.scrollTo(buttons.indexOf(button));
    setCurrentSlide(buttons.indexOf(button));
  };

  return (
    <div>
      <div className="flex flex-wrap sm:flex-nowrap gap-1 sm:gap-2 mx-auto w-full justify-center mb-4 sm:mb-8 font-thin px-2 sm:px-0">
        {buttons.map((button) => (
          <Button
            key={button}
            size="sm"
            variant="outline"
            className={cn(
              'border-none bg-[#171717] text-primary text-xs sm:text-sm',
              currentSlide === buttons.indexOf(button)
                ? 'text-white bg-primary'
                : ''
            )}
            onClick={() => {
              handleButtonClick(button);
            }}
          >
            {button}
          </Button>
        ))}
      </div>
      <div className="embla max-w-[1360px] mx-auto overflow-x-hidden px-2 sm:px-0">
        <div ref={emblaRef} className="embla__viewport">
          <div className="flex touch-pan-y touch-pinch-zoom">
            {kreateItems.map((item) => (
              <KreateItem key={item.title} item={item} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KreateSomethingCarousel;
